/// <reference types="@types/google.maps" />
import { JobOverviewPage } from "./jobOverviewPage";
import * as JobTypes from "../types/jobData";
// @ts-ignore – falls TS meckert, weil MarkerClusterer global ist
// @ts-ignore
// import { MarkerClusterer } from "@googlemaps/markerclusterer";

declare const jobData: any;

// map.ts
export class MapGoogle {
  private jobOverviewPage: JobOverviewPage | null = null; // Parent page reference
  private map: google.maps.Map | null = null;
  private markers: google.maps.Marker[] = []; // statt AdvancedMarkerElement
  private clusterer: MarkerClusterer | null = null;
  private markerJobMap: globalThis.Map<google.maps.Marker, { job: JobTypes.jobData; location: any }> =
    new globalThis.Map();

  private infoWindow: google.maps.InfoWindow | null = null;

  // ?
  private siteURL = window.location.origin + window.location.pathname;

  // graphics
  private pinImage = jobData.pinImage;
  private clusterImg = jobData.clusterImg;

  // DOM elements
  private container: HTMLElement | null = null;
  private overlay: HTMLElement | null = null; // results overlay on map

  private currentLang: string = "de"; // default language
  constructor() {
    this.currentLang = jobData.currentLang;
  }

  async init(parentPage: JobOverviewPage) {
    this.jobOverviewPage = parentPage || null;
    await this.initMap();
    this.createJobOverlay();
    this.addMarkers(this.jobOverviewPage!.getFilteredJobs); // Pass the jobs data to addMarkers
    // this.initFullscreenOverlayEvents();
    this.mapZoomEvent(); // Listen for zoom events to get visible jobs
  }

  private mapZoomEvent() {
    this.map?.addListener("idle", () => {
      const visibleJobs = this.getVisibleJobs();
      this.jobOverviewPage!.updateJobsOnMapSidebar(visibleJobs); // Update the sidebar with visible jobs
    });
  }

  public reloadMarkers() {
    if (this.map) {
      this.removeMarkers(); // Clear existing markers
      this.addMarkers(this.jobOverviewPage!.getFilteredJobs); // Add new markers based on the current filtered jobs
    }
  }

  /**
   * Creates the overlay for job details. Which is shown when a marker is clicked.
   */
  private createJobOverlay() {
    this.overlay = document.createElement("div");
    this.overlay.classList.add("marker_results_overlay");
    this.container!.appendChild(this.overlay); // in .djp_map einfügen
  }

  /**
   * Initializes the Google Map. And sets the default options.
   */
  async initMap() {
    this.container = document.querySelector(".djp_map") as HTMLElement;
    if (!this.container) {
      console.error("Container for map not found.");
      return;
    }

    await google.maps.importLibrary("maps"); // Only what we need
    await google.maps.importLibrary("marker");

    if (this.currentLang == "de") {
      this.map = new google.maps.Map(this.container, {
        center: { lat: 50.1109, lng: 8.6821 }, // Frankfurt als Mittelpunkt
        zoom: 6, // besserer Fokus auf Europa
        maxZoom: 10,
        disableDefaultUI: true,
      });
    } else {
      this.map = new google.maps.Map(this.container, {
        center: { lat: 0, lng: 0 },
        zoom: 2,
        maxZoom: 10,
        disableDefaultUI: true,
      });
    }

    this.map.setOptions({ zoomControl: true });

    // console.log("✅ Map initialized:", this.map);
  }

  public getVisibleJobs(): JobTypes.jobData[] {
    if (!this.map || !this.markerJobMap) return [];

    const bounds = this.map.getBounds();
    if (!bounds) return [];

    const visibleJobs: JobTypes.jobData[] = [];

    this.markers.forEach((marker) => {
      if (bounds.contains(marker.getPosition()!)) {
        const mapping = this.markerJobMap.get(marker);
        if (mapping) {
          visibleJobs.push(mapping.job);
        }
      }
    });

    return visibleJobs;
  }

  private addMarkers(jobs: JobTypes.jobData[]) {
    for (const job of jobs) {
      const locations = job.jobOpening?.locations;

      if (!Array.isArray(locations) || locations.length === 0) {
        console.warn("🚫 Keine gültigen Locations für Job:", job);
        continue;
      }

      for (const loc of locations) {
        if (!loc.latitude || !loc.longitude) {
          console.warn("⚠️ Ungültige Location (keine Koordinaten):", loc);
          continue;
        }

        const marker = new google.maps.Marker({
          map: this.map!,
          position: {
            lat: loc.latitude,
            lng: loc.longitude,
          },
          icon: {
            url: this.clusterImg,
            scaledSize: new google.maps.Size(40, 40),
          },
          title: `${job.pageTitle} – ${loc.name || loc.address?.city || ""}`,
        });

        marker.addListener("click", () => {
          this.infoWindow ??= new google.maps.InfoWindow();
          this.infoWindow.setContent(this.createInfoContent(job, loc));
          this.infoWindow.open(this.map, marker);

          this.renderOverlayForJob(job, loc);
        });

        this.markers.push(marker);
        this.markerJobMap.set(marker, { job, location: loc });
      }
    }

    this.createGlobalClusterer(); // Clusterer erstellen
    this.handleClustererClick(); // Cluster-Click-Event hinzufügen

    // console.log("✅ Marker added:", this.markers.length);
  }

  /**
   * Creates the clusterer for the map. This is the global clusterer. (merges all markers in a space into one cluster)
   * @returns {void}
   */
  private createGlobalClusterer() {
    if (this.clusterer) {
      this.clusterer.clearMarkers(); // important in case of reloading the map
    }

    // @ts-ignore – globale MarkerClusterer-Version (alte Legacy-Version)
    this.clusterer = new MarkerClusterer(this.map!, this.markers, {
      styles: [
        {
          url: this.clusterImg, // dein eigenes Cluster-Bild (PNG, z. B. runder Button)
          height: 40, // Bildhöhe in px
          width: 40, // Bildbreite in px
          textColor: "white", // Farbe der Zahl
          textSize: 14, // Schriftgröße in px
          anchorText: [0, 0], // optional: zentriere Text, falls nötig
        },
      ],
      gridSize: 60, // Abstand in Pixeln für Clustering (default = 60)
      minimumClusterSize: 1, // ab wie vielen Markern geclustert wird
      maxZoom: 14, // bis zu welchem Zoom-Level geclustert wird
    });
  }

  private handleClustererClick() {
    // @ts-ignore
    google.maps.event.addListener(this.clusterer, "clusterclick", (cluster: any) => {
      const markersInCluster = cluster.getMarkers();
      const jobsInCluster = markersInCluster.map((marker: any) => this.markerJobMap.get(marker)).filter(Boolean) as {
        job: JobTypes.jobData;
        location: any;
      }[];

      if (jobsInCluster.length === 0) return;

      // Position für InfoWindow: Cluster-Mittelpunkt
      const position = cluster.getCenter();

      // HTML-Content bauen
      const jobCardsHtml = this.buildClusterInfoWindowContent(jobsInCluster);

      // InfoWindow anzeigen
      this.infoWindow ??= new google.maps.InfoWindow();
      this.infoWindow.setContent(jobCardsHtml);
      this.infoWindow.setPosition(position);
      this.infoWindow.open(this.map);
    });
  }

  private buildClusterInfoWindowContent(jobsInCluster: { job: JobTypes.jobData; location: any }[]): string {
    return `
      <div class="job-map-infowindow">
        ${jobsInCluster
          .map(({ job, location }) => {
            const startDate = job.jobOpening.earliestEntryDate || "Startdatum fehlt";
            const city = location.address?.city || location.name || "Ort fehlt";
            const zip = location.address?.zipCode || "";
            const jobURL = `${this.siteURL}${job.id}`;

            return `
              <div class="job-map-card">
                <a href="${jobURL}" class="job-map-card__link-container">
                  <div class="job-map-card__start">${startDate}</div>
                  <div class="job-map-card__title">${job.pageTitle}</div>
                  <div class="job-map-card__location">${city} ${zip}</div>
                </a>
              </div>
            `;
          })
          .join("")}
      </div>
    `;
  }

  private createInfoContent(job: JobTypes.jobData, location: any): string {
    return `
      <strong>${job.pageTitle}</strong><br>
      ${location.name || ""}<br>
      ${location.address?.city || ""} ${location.address?.zipCode || ""}
    `;
  }

  private renderOverlayForJob(job: JobTypes.jobData, location: any) {
    if (!this.overlay) return;

    const startDate = job.jobOpening.earliestEntryDate || "Startdatum fehlt";
    const city = location.address?.city || location.name || "Ort fehlt";
    const zip = location.address?.zipCode || "";
    const jobURL = `${this.siteURL}${job.id}`;
    console.log("console.log jobURL:", jobURL);

    this.overlay.innerHTML = `
      <div class="job-map-card">
        <a href="${jobURL}" class="job-map-card__link-container">
          <div class="job-map-card__start">
            ${startDate}
          </div>
          <div class="job-map-card__title">
            ${job.pageTitle}
          </div>
          <div class="job-map-card__location">
            ${city} ${zip}
          </div>
        </a>
      </div>
    `;

    this.overlay.style.display = "block";
  }

  private createMarkerContent(text: string): HTMLElement {
    const div = document.createElement("div");
    div.innerText = text;
    div.classList.add("custom-marker"); // kannst du stylen per CSS
    return div;
  }

  private removeMarkers() {
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
  }

  /**
   * Handles the fullscreen map overlay open/close logic.
   */
  // private initFullscreenOverlayEvents() {
  //   const openBtn = document.querySelector(".map-overlay-button");
  //   const closeBtn = document.querySelector(".map-overlay-close");
  //   const overlay = document.getElementById("fullscreenMapOverlay");
  //   const body = document.querySelector("body");
  //   const header = document.querySelector(".elementor-location-header");

  //   openBtn?.addEventListener("click", () => {
  //     if (overlay) overlay.style.display = "block";
  //     window.scrollTo(0, 0);
  //     if (body) body.style.overflow = "hidden";
  //     header?.classList.add("map-is-open");
  //   });

  //   closeBtn?.addEventListener("click", () => {
  //     if (this.overlay?.classList.contains("active")) {
  //       this.overlay.classList.remove("active");
  //       return;
  //     }

  //     if (overlay) overlay.style.display = "none";
  //     if (body) body.style.overflow = "auto";
  //     header?.classList.remove("map-is-open");
  //   });
  // }
}
